import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import './Header.css'
import { useI18n } from '@/lib/i18n'
import { SECTION_PATHS, type Section } from '@/lib/sections'
import { transitionTo } from '@/lib/transitions'
import LanguageSwitcher from '@/components/ui/LanguageSwitcher/LanguageSwitcher'
import ThemeSwitcher from '@/components/ui/ThemeSwitcher/ThemeSwitcher'
import Wordmark from '@/components/ui/Wordmark'

type Props = { active: Section; onNavigate: (s: Section) => void }

const NAV: Section[] = ['projects', 'about', 'faq', 'contact']

export default function Header({ active, onNavigate }: Props) {
  const { t, to } = useI18n()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isShell = Object.values(SECTION_PATHS).includes(pathname)
  const [open, setOpen] = useState(false)

  const onArchive = /^\/(?:en\/)?archive(?:\/|$)/.test(pathname)
  const goArchive = () => {
    setOpen(false)
    transitionTo(navigate, to('/archive'))
  }

  const go = (target: Section) => {
    setOpen(false)
    if (!isShell) transitionTo(navigate, to(SECTION_PATHS[target]))
    else onNavigate(target)
  }

  // Lock scroll while the mobile menu is open, close on Escape
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <header className="header">
      <div className="container header__inner">
        <Wordmark onClick={() => go('home')} />

        <nav className="header__nav" aria-label="primary">
          {NAV.map((s) => (
            <button
              key={s}
              type="button"
              className="header__link"
              aria-current={active === s ? 'page' : undefined}
              onClick={() => go(s)}
            >
              <span className="header__label">{t(`nav.${s}`)}</span>
            </button>
          ))}
          <button
            type="button"
            className="header__link"
            aria-current={onArchive ? 'page' : undefined}
            onClick={goArchive}
          >
            <span className="header__label">{t('nav.archive')}</span>
          </button>
        </nav>

        <div className="header__controls">
          <LanguageSwitcher />
          <ThemeSwitcher />
          <button
            type="button"
            className="header__burger"
            aria-label={open ? t('nav.close') : t('nav.menu')}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((o) => !o)}
          >
            <span className="mono">{open ? t('nav.close') : t('nav.menu')}</span>
          </button>
        </div>
      </div>

      <div id="mobile-nav" className={`header__mobile ${open ? 'is-open' : ''}`} aria-hidden={!open}>
        <div className="container header__mobile-inner">
          {NAV.map((s, i) => (
            <button
              key={s}
              type="button"
              className="header__mobile-link"
              aria-current={active === s ? 'page' : undefined}
              onClick={() => go(s)}
              tabIndex={open ? 0 : -1}
            >
              <span className="mono muted">0{i + 1}</span>
              {t(`nav.${s}`)}
            </button>
          ))}
          <button
            type="button"
            className="header__mobile-link"
            aria-current={onArchive ? 'page' : undefined}
            onClick={goArchive}
            tabIndex={open ? 0 : -1}
          >
            <span className="mono muted">0{NAV.length + 1}</span>
            {t('nav.archive')}
          </button>
          <div className="header__mobile-controls">
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
        </div>
      </div>
    </header>
  )
}
